/**
 * The trainer's own question shelf — `/raporty/katalog`, and the „Z katalogu" half of the editor's
 * „Nowe pytanie" card (R35).
 *
 * ── A shelf entry is a STENCIL, not a live reference ─────────────────────────────────────────
 * This is the one thing the whole screen turns on, and the delete dialog's copy states it out loud.
 * Inserting a shelf question into a survey COPIES its label, its answer type and its options into
 * the template's own item and records where it came from in `sourceQuestionId`; nothing is read back
 * through that id at render time. So deleting a shelf row removes the stencil and leaves every
 * survey that used it exactly as it was — which is also the only reading compatible with R12, where
 * a question is frozen at the moment a survey is sent.
 *
 * `usedIn` is the backend's own reverse lookup over those `sourceQuestionId`s. It is read, never
 * computed here: this screen sees the shelf and not the templates, so a count of our own would be a
 * count of the surveys we happened to have in cache.
 *
 * ── The two filters are a NARROWING, never a request ─────────────────────────────────────────
 * `GET /survey/question-catalog` takes no parameters and returns the trainer's whole shelf, which is
 * a shelf of dozens rather than thousands. Filtering it here costs one pass and keeps the „N pytań"
 * figure and the rows in agreement, which a per-filter request would not.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type {
  TrainWithMeModulesSurveysDtosResponsesTrainerQuestionListResponse as TrainerQuestionListResponse,
  TrainWithMeModulesSurveysDtosResponsesTrainerQuestionResponse as TrainerQuestionResponse,
} from '~/shared/api/generated/model';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import type { TranslateKey } from './translateKey';

/** One survey this question is in. The backend's own reverse lookup. */
export interface ShelfUsage {
  readonly templateId: number;
  readonly title: string;
}

/** One row of the shelf. */
export interface ShelfQuestion {
  readonly id: number;
  readonly label: string;
  /** The backend's spelling of `AnswerType`. Never narrowed here — the shelf shows what it holds. */
  readonly answerType: string;
  readonly options: readonly string[];
  /** A number question's unit. The shelf stores one; a TEMPLATE item cannot — see `AGENTS.md`. */
  readonly unit: string | null;
  readonly required: boolean;
  /** ISO instant. `null` where the wire omitted it, which the row then simply does not date. */
  readonly savedAt: string | null;
  readonly usedIn: readonly ShelfUsage[];
}

/** What the shelf is narrowed by. */
export interface ShelfFacets {
  readonly term: string;
  /** One `AnswerType` spelling, or `null` for every type. */
  readonly answerType: string | null;
  readonly usage: 'all' | 'used' | 'unused';
}

export const NO_SHELF_FACETS: ShelfFacets = { term: '', answerType: null, usage: 'all' };

function usageOf(question: TrainerQuestionResponse): readonly ShelfUsage[] {
  return (question.usedIn ?? []).flatMap((entry) =>
    typeof entry.templateId === 'number' ? [{ templateId: entry.templateId, title: entry.title ?? '' }] : [],
  );
}

/** The response as rows, newest first — the order the canon lists them in. */
export function shelfQuestions(
  response: TrainerQuestionListResponse | null | undefined,
): readonly ShelfQuestion[] {
  const rows = (response?.questions ?? []).flatMap((question): ShelfQuestion[] => {
    // The id is the row's whole identity — it is what the pencil patches, what the trash deletes
    // and what an inserted copy records as its provenance — so a row without one is dropped rather
    // than drawn as a control that cannot act.
    if (typeof question.id !== 'number') return [];
    return [
      {
        id: question.id,
        label: question.label ?? '',
        answerType: question.answerType ?? '',
        options: question.options ?? [],
        unit: question.unit ?? null,
        required: question.required === true,
        savedAt: question.createdAt ?? null,
        usedIn: usageOf(question),
      },
    ];
  });

  return [...rows].sort((left, right) => (right.savedAt ?? '').localeCompare(left.savedAt ?? ''));
}

/** Case-folded and stripped of combining marks, so „nagranie" finds „Nagraj". */
function searchable(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** The shelf as the trainer narrowed it. */
export function filterShelf(
  questions: readonly ShelfQuestion[],
  facets: ShelfFacets,
): readonly ShelfQuestion[] {
  const needle = searchable(facets.term.trim());
  return questions.filter((question) => {
    if (needle !== '' && !searchable(question.label).includes(needle)) return false;
    if (facets.answerType !== null && question.answerType !== facets.answerType) return false;
    if (facets.usage === 'used' && question.usedIn.length === 0) return false;
    if (facets.usage === 'unused' && question.usedIn.length > 0) return false;
    return true;
  });
}

/** Whether anything is hidden — what the empty column's „Wyczyść filtry" is offered for. */
export function shelfIsNarrowed(facets: ShelfFacets): boolean {
  return facets.term.trim() !== '' || facets.answerType !== null || facets.usage !== 'all';
}

/** Every answer type the shelf actually holds, so the filter never offers an empty result. */
export function shelfAnswerTypes(questions: readonly ShelfQuestion[]): readonly string[] {
  return [...new Set(questions.map((question) => question.answerType).filter((type) => type !== ''))].sort();
}

/**
 * What the trash asks for.
 *
 * The description states the stencil rule rather than warning about it, because the act is far less
 * destructive than the icon suggests and a trainer who is not told will assume the opposite.
 */
export function shelfDeletePrompt(
  question: ShelfQuestion,
  t: TranslateKey,
  busy: boolean,
): ConfirmPrompt {
  return {
    title: t('web:reports.catalog.delete.title'),
    // Not interpolated: Polish would decline the question's own wording inside the sentence.
    subject: question.label,
    description:
      question.usedIn.length === 0
        ? t('web:reports.catalog.delete.bodyUnused')
        : t('web:reports.catalog.delete.bodyUsed', { count: question.usedIn.length }),
    confirmLabel: t('web:reports.catalog.delete.confirm'),
    cancelLabel: t('web:reports.catalog.delete.cancel'),
    destructiveConfirm: true,
    busy,
  };
}
